import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import {
  ArrowRight,
  Award,
  BedDouble,
  ChevronRight,
  CircleDot,
  Clock,
  GraduationCap,
  Layers,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  PersonStanding,
  Plus,
  Sparkles,
  User,
  Zap,
} from "lucide-react";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";

const PAGE_URL = "https://drvickyjain.com/mommy-makeover-surgery-mumbai";

const MOMMY_MAKEOVER_FAQS = [
  {
    question: "Can all mommy makeover procedures be done in one surgery?",
    answer:
      "In most cases, yes. The standard combination of breast procedures with tummy tuck and liposuction is routinely performed in a single session of 4 to 8 hours. The limiting factor is total safe surgical time and anesthesia duration. Patients who need more extensive correction across more areas than a single safe session allows are staged, with the highest-priority concerns addressed first and remaining procedures planned for a second surgery.",
  },
  {
    question: "When can I lift my baby or young child after mommy makeover?",
    answer:
      "Lifting is restricted for 4 to 6 weeks after surgery to protect the abdominal muscle repair and breast incisions. This is one of the most practical planning considerations for mothers with young children. Having sufficient post-operative support from a partner, family member, or caregiver during the first 4 to 6 weeks is essential for a safe recovery.",
  },
  {
    question: "Will I be able to breastfeed after mommy makeover breast surgery?",
    answer:
      "This depends on which breast procedure is performed and the specific technique used. Mastopexy using pedicle techniques that preserve the nipple's ductal connections often maintains breastfeeding function. Augmentation with incisions that do not cross the areola generally does not affect breastfeeding. Patients who want to preserve the option of breastfeeding should discuss this explicitly during consultation so the surgical plan accounts for it.",
  },
  {
    question: "How long do mommy makeover results last?",
    answer:
      "Results are long-lasting provided weight remains stable and no future pregnancies occur. The muscle repair from the tummy tuck is permanent. The skin removal is permanent. Breast results last 8 to 12 years before aging, gravity, and weight changes create changes that may benefit from revision. Maintaining a stable weight and wearing proper breast support extends all results.",
  },
  {
    question: "Is a mommy makeover safe?",
    answer:
      "Yes, when performed by an experienced plastic surgeon who plans the procedure combination within safe surgical duration limits. The risks are those associated with each individual procedure: swelling, infection, seroma, wound healing delays, scarring, and anesthesia risks. These are minimized by proper patient selection, appropriate staging when needed, smoking cessation, and post-operative care compliance.",
  },
  {
    question: "How soon after pregnancy can I have a mommy makeover?",
    answer:
      "The minimum timeline is 6 months after delivery, after stopping breastfeeding for at least 3 to 6 months, and after reaching a stable weight. For most patients this means surgery happens 12 to 18 months after their last delivery at the earliest. Operating earlier produces results that change as breastfeeding hormones normalize and post-pregnancy weight fluctuates.",
  },
  {
    question: "Will a mommy makeover help with stretch marks?",
    answer:
      "Stretch marks below the navel are removed with the excised abdominal skin during tummy tuck and are gone permanently. Stretch marks above the navel are not removed but may improve slightly as the surrounding skin is tightened. Stretch marks on the breasts, hips, and thighs are not specifically treated by mommy makeover procedures.",
  },
  {
    question: "What is included in a mommy makeover consultation?",
    answer:
      "Dr. Jain performs a detailed assessment of all areas of concern including breast position, volume, and skin quality, abdominal skin laxity, muscle separation, and fat distribution. Goals are discussed in the context of what each procedure realistically achieves. A surgical plan is proposed specifying which procedures, which techniques, and whether a single or staged approach is appropriate. Timing recommendations, recovery planning, and cost breakdown are all provided at consultation.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalProcedure",
      name: "Mommy makeover surgery",
      description:
        "Customized combination cosmetic surgery addressing post-pregnancy changes to the breasts, abdomen, and related areas; procedures and staging are individualized per patient for a coordinated aesthetic result.",
      procedureType: "SurgicalProcedure",
      bodyLocation: [
        { "@type": "AnatomicalStructure", name: "Breast" },
        { "@type": "AnatomicalStructure", name: "Abdomen" },
      ],
      url: PAGE_URL,
      medicalSpecialty: "https://schema.org/PlasticSurgery",
    },
    {
      "@type": "FAQPage",
      mainEntity: MOMMY_MAKEOVER_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function MommyMakeoverJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

const Breadcrumb = () => {
  const items = [
    { name: "Home", href: "/" },
    { name: "Cosmetic Surgery", href: "/#services" },
    { name: "Body", href: "/#services" },
    { name: "Mommy Makeover", href: "/mommy-makeover-surgery-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
};

const HeroSection = () => (
  <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Cosmetic Surgery · Body</p>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">Mommy Makeover Surgery</h1>
          <div className="w-12 h-[2px] bg-gold mb-5" />
          <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
            Post-Pregnancy Body Restoration by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
          </p>
          <div className="flex flex-wrap gap-2">
            <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">Board Certified</span>
            <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">17+ Years Experience</span>
            <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">International Training</span>
          </div>
        </div>
        <div className="flex justify-center lg:justify-end">
          <ProcedurePlaceholder procedureName="Mommy Makeover" icon="🌺" size="hero"  imageSrc={HERO.mommyMakeover} imageAlt="Mommy makeover"/>
        </div>
      </div>
    </div>
  </section>
);

const WhatIsSection = () => (
  <section className="bg-warm-white py-14 md:py-20" data-testid="what-is-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">What is a Mommy Makeover?</h2>
        </div>
        <div className="space-y-6 text-deep-navy/70 leading-relaxed">
          <p>
            A mommy makeover is a customized combination of cosmetic surgical procedures designed to address the specific body changes caused by pregnancy, childbirth, and breastfeeding. There is no single fixed combination. The procedures selected depend on which areas are affected, what the degree of change is in each area, and what the patient&apos;s goals are. Most mommy makeovers address the breasts, abdomen, and waist as the areas most consistently altered by pregnancy.
          </p>
          <p>
            Pregnancy produces a predictable pattern of physical change: breast volume increases then reduces after breastfeeding, leaving deflated or sagging breast tissue; the abdominal skin stretches beyond its ability to retract and the rectus muscles separate along the midline; and fat redistributes to the waist, flanks, and hips in a pattern that often persists despite weight loss. These are structural changes. Exercise and diet address fat and muscle conditioning but do not remove stretched skin, restore breast position, or repair separated muscles.
          </p>
          <p>
            Dr. Vicky Jain designs each mommy makeover plan individually based on a thorough assessment of what has changed and what each patient wants to restore. Combining procedures into a single surgery means one anesthesia, one recovery period, and a coordinated result rather than multiple unrelated procedures done separately over years.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const DrJainApproachSection = () => (
  <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Approach</p>
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Mommy Makeover</h2>
          <div className="space-y-6 text-white/75 leading-relaxed">
            <p>
              The most important decision in mommy makeover planning is what to include and what to stage. Combining too many procedures in a single session increases surgical time, anesthesia exposure, and complication risk beyond what is safe. Dr. Jain determines the appropriate combination per session based on the patient&apos;s health status, the extent of each procedure required, and the total safe surgical duration. A tummy tuck combined with breast procedures is a common and manageable combination. Adding liposuction of the flanks alongside this is often appropriate. Adding a thigh lift or body lift to the same session may require staging.
            </p>
            <p>
              The sequence of procedures within a single session is also planned deliberately. Breast work and tummy tuck require different patient positioning. Dr. Jain coordinates the procedure order to minimize repositioning time and ensure each component is performed with optimal access. The result is a coordinated transformation where every changed area has been addressed according to a single coherent aesthetic plan rather than individual procedures assembled without reference to each other.
            </p>
          </div>
        </div>
        <div className="relative">
          <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
          <div
            className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
            style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
          />
          <div className="mt-4 text-center lg:text-left">
            <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const PostPregnancyChangesSection = () => {
  const cards = [
    {
      title: "Sagging or Deflated Breasts",
      icon: User,
      copy: "Breastfeeding and post-pregnancy volume loss leave breasts that sit lower, have less fullness in the upper pole, and in many cases have a nipple that points downward rather than forward. Mastopexy restores position, augmentation restores volume, or both are combined when both changes are present.",
    },
    {
      title: "Loose Abdominal Skin",
      icon: Layers,
      copy: "Skin that stretched beyond its elasticity limit during pregnancy does not fully retract after delivery. The degree of residual laxity depends on the number of pregnancies, the size of the baby, and individual skin quality. Tummy tuck removes this excess skin surgically.",
    },
    {
      title: "Diastasis Recti",
      icon: Zap,
      copy: "Separation of the rectus abdominis muscles along the midline creates a visible central bulge that exercises do not correct and often worsen. Muscle repair during tummy tuck closes this gap, narrows the waist, and restores abdominal wall function.",
    },
    {
      title: "Stubborn Post-Pregnancy Fat",
      icon: CircleDot,
      copy: "Fat that redistributes to the waist, flanks, and hips during pregnancy and does not reduce despite returning to pre-pregnancy weight. Liposuction removes this localized fat and restores the waistline that pregnancy widened.",
    },
    {
      title: "Stretch Marks",
      icon: Sparkles,
      copy: "Stretch marks below the navel are removed with the excised abdominal skin during tummy tuck. Stretch marks above the navel may improve in appearance as the surrounding skin is tightened but are not removed surgically.",
    },
    {
      title: "Overall Loss of Body Proportion",
      icon: Monitor,
      copy: "Pregnancy changes multiple areas simultaneously, creating a cumulative effect on body proportion that no single procedure addresses. The mommy makeover approach restores proportion across all affected areas together rather than improving one area in isolation.",
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="post-pregnancy-changes-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Post-Pregnancy Changes a Mommy Makeover Corrects</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed text-sm md:text-base">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ProceduresIncludedSection = () => {
  const blocks = [
    {
      title: "Breast Lift (Mastopexy)",
      copy: "For patients whose primary breast concern is position. The nipple has descended and the breast tissue has lost its upper fullness and youthful contour. Mastopexy removes excess skin, reshapes the breast tissue, and repositions the nipple to a more youthful level. Used alone when breast volume is adequate and the concern is position only.",
    },
    {
      title: "Breast Augmentation",
      copy: "For patients whose primary concern is volume loss rather than position. Implants or fat transfer restore the breast volume lost after breastfeeding. Used alone when position is acceptable and volume restoration is the primary goal. Combined with mastopexy when both position and volume are affected.",
    },
    {
      title: "Breast Augmentation with Lift",
      copy: "The most common breast procedure component of a mommy makeover. Addresses both the deflation and the positional descent that occur together in most patients after breastfeeding. Planned as a combined procedure where both the lift technique and implant selection are coordinated.",
    },
    {
      title: "Tummy Tuck (Abdominoplasty)",
      copy: "Removes excess abdominal skin, repairs the separated rectus muscles, and removes localized abdominal fat. The most consistently included procedure in a mommy makeover as abdominal changes after pregnancy are nearly universal. The incision is placed within the underwear line and the belly button is repositioned.",
    },
    {
      title: "Liposuction",
      copy: "Removes stubborn post-pregnancy fat from the flanks, waist, hips, and thighs that did not respond to weight loss after delivery. Frequently combined with tummy tuck as part of the lipoabdominoplasty approach to produce a more complete waistline result than tummy tuck alone.",
    },
    {
      title: "Optional Additions",
      copy: "Buttock augmentation for patients who want improved lower body proportion alongside abdominal correction. Arm lift or thigh lift for patients with skin excess in these areas. Vaginal rejuvenation for patients with functional or aesthetic concerns related to delivery. These are staged or combined depending on extent and safety considerations.",
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="procedures-included-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">What Procedures Are Part of a Mommy Makeover?</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {blocks.map((block) => (
            <div key={block.title} className="bg-white p-8 border border-[#E8E2D9]">
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{block.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{block.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const TimingPlanningSection = () => (
  <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="timing-planning-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Timing</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">When is the Right Time for a Mommy Makeover?</h2>
        </div>
        <div className="space-y-6 text-deep-navy/70 leading-relaxed">
          <p>
            The appropriate timing for mommy makeover surgery is after the patient has completed their family, has stopped breastfeeding for at least 3 to 6 months, and has reached a stable weight maintained for at least 3 to 6 months. Each of these conditions matters independently. Future pregnancy after a mommy makeover reverses the abdominal muscle repair and skin tightening and can cause breast changes that undo the mastopexy or augmentation result. Operating before completing the family is not recommended.
          </p>
          <p>
            Breastfeeding maintains elevated progesterone levels that affect breast tissue volume and texture. Operating on the breast while breastfeeding hormones are active produces a result that changes when those hormones normalize. Waiting at least 3 to 6 months after stopping breastfeeding ensures the breast has settled to its stable post-breastfeeding volume and position before it is surgically addressed. Weight stability ensures the abdominal skin and fat distribution have reached their resting state after delivery before surgical correction is planned around them.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const HowSurgeryPerformedSection = () => (
  <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-surgery-performed-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">How Mommy Makeover Surgery is Performed</h2>
        </div>
        <div className="space-y-6 text-deep-navy/70 leading-relaxed">
          <p>
            Surgery takes 4 to 8 hours under general anesthesia depending on the combination of procedures. Dr. Jain coordinates the procedure sequence within the session to minimize repositioning time and ensure each component is performed with optimal access. Breast procedures are typically performed with the patient in a semi-reclined position. Tummy tuck is performed supine. Liposuction can be performed in either position depending on the areas treated.
          </p>
          <p>
            The tummy tuck component includes muscle repair before skin removal. The breast component includes implant placement, mastopexy, or both, depending on what was planned. Liposuction of the flanks and waist is typically performed before the tummy tuck incision is made, as the tumescent technique used in liposuction reduces bleeding during the subsequent skin work.
          </p>
          <p>
            Compression garments are applied to the treated areas immediately after surgery. Drains may be placed for the tummy tuck component and are removed within 5 to 7 days. Most patients stay one to two nights. A detailed post-operative care plan is provided at discharge covering each procedure&apos;s specific requirements.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const RecoveryTimelineSection = () => {
  const milestones = [
    { period: "Week 1", description: "Swelling, bruising, and tightness across all treated areas. Drains in place. Assistance with daily activities is needed. Rest is the priority. Most patients are mobile but significantly limited.", icon: BedDouble },
    { period: "Weeks 2 to 3", description: "Drains removed. Light daily activity resumes. The improvement across all treated areas is visible even through residual swelling. Most patients can manage independently at home.", icon: PersonStanding },
    { period: "Weeks 3 to 4", description: "Return to desk work for most patients. Compression garments continue. Strenuous activity, heavy lifting, and exercise remain restricted. Child lifting resumes after 4 to 6 weeks.", icon: Monitor },
    { period: "Months 2 to 3", description: "Full activity including exercise resumes progressively from 6 weeks. Final results across all treated areas are visible at 2 to 3 months as all swelling resolves. Scars begin their maturation process.", icon: Sparkles },
  ];
  return (
    <section className="bg-premium-purple py-14 md:py-20" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">What to Expect</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={index} className="relative">
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={index} className="relative">
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const ComparisonSection = () => {
  const rows = [
    {
      label: "Recovery Periods",
      combined: "One recovery period for all procedures combined.",
      separate: "Separate recovery for each procedure, often 3 to 5 separate recoveries over 2 to 4 years.",
    },
    {
      label: "Anesthesia Exposure",
      combined: "One general anesthesia for all procedures.",
      separate: "Separate anesthesia for each procedure.",
    },
    {
      label: "Result Coordination",
      combined: "All areas planned together for a unified aesthetic result.",
      separate: "Each area addressed independently without coordination with other changes.",
    },
    {
      label: "Time Away from Work and Family",
      combined: "One extended absence.",
      separate: "Multiple shorter absences totaling significantly more total time.",
    },
    {
      label: "Overall Cost",
      combined: "More cost-effective than the same procedures done separately due to combined facility, anesthesia, and surgical time efficiencies.",
      separate: "Higher cumulative cost across multiple separate surgical episodes.",
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="comparison-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Understanding Your Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Mommy Makeover vs. Individual Procedures Done Separately</h2>
        <div className="max-w-5xl mx-auto border border-[#E8E2D9] bg-white overflow-hidden">
          <div className="hidden md:grid md:grid-cols-3 gap-0 border-b border-[#E8E2D9] bg-premium-purple text-warm-white">
            <div className="p-4 font-serif text-sm md:text-base" />
            <div className="p-4 font-serif text-sm md:text-base border-l border-warm-white/20">Mommy Makeover</div>
            <div className="p-4 font-serif text-sm md:text-base border-l border-warm-white/20">Individual Procedures</div>
          </div>
          {rows.map((row) => (
            <div
              key={row.label}
              className="grid grid-cols-1 md:grid-cols-3 gap-0 border-b border-[#E8E2D9] last:border-b-0"
            >
              <div className="p-4 md:p-5 bg-warm-white/80 font-medium text-deep-navy text-sm md:text-base">{row.label}</div>
              <div className="p-4 md:p-5 border-t md:border-t-0 md:border-l border-[#E8E2D9] text-deep-navy/70 text-sm leading-relaxed">
                <span className="md:hidden font-serif text-premium-purple block mb-2">Mommy Makeover</span>
                {row.combined}
              </div>
              <div className="p-4 md:p-5 border-t md:border-t-0 md:border-l border-[#E8E2D9] text-deep-navy/70 text-sm leading-relaxed">
                <span className="md:hidden font-serif text-premium-purple block mb-2">Individual Procedures</span>
                {row.separate}
              </div>
            </div>
          ))}
        </div>
        <p className="max-w-5xl mx-auto mt-8 text-deep-navy/70 text-sm md:text-base leading-relaxed text-center md:text-left">
          The limitation of combining procedures is total surgical time and safety. Dr. Jain plans each mommy makeover within safe surgical duration limits. Patients who need more procedures than a single session safely allows are staged with the highest-priority concerns addressed first.
        </p>
      </div>
    </section>
  );
};

const GoodCandidateSection = () => (
  <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="good-candidate-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Am I a Candidate?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">Who Is a Good Candidate for Mommy Makeover?</h2>
        </div>
        <div className="space-y-6 text-deep-navy/70 leading-relaxed">
          <p>
            Good candidates are women who have completed their family, are at a stable weight maintained for at least 3 to 6 months after delivery, have stopped breastfeeding for at least 3 to 6 months, and have specific post-pregnancy concerns with their breasts, abdomen, or both that have not resolved with time and lifestyle measures. Most mommy makeover patients are in their late 20s to early 40s, though there is no strict age limit for women in good health.
          </p>
          <p>
            Being close to the weight you plan to maintain long-term before surgery produces the best result. Significant weight loss after a mommy makeover affects the tightened abdominal skin and the breast result. Significant weight gain stretches the corrected areas. Non-smokers or patients who stop smoking at least 4 weeks before surgery have better wound healing. Patients with young children should plan their surgery for a period when adequate post-operative support is available, as the first 2 weeks require limited lifting and physical activity that is difficult to maintain while caring for young children without help.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const PatientResultsSection = () => <PatientTestimonials procedureName="Mommy Makeover" />;

const FAQSection = () => (
  <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
      <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
      <div className="max-w-3xl mx-auto">
        <Accordion type="single" collapsible className="w-full">
          {MOMMY_MAKEOVER_FAQS.map((faq, index) => (
            <AccordionItem key={faq.question} value={`mommy-${index}`} className="border-b border-deep-navy/10">
              <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                <span className="pr-4">{faq.question}</span>
                <div className="flex-shrink-0">
                  <Plus className="w-5 h-5 text-gold" />
                  <Minus className="w-5 h-5 text-gold hidden" />
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  </section>
);

const ConsultationCTASection = () => (
  <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
      <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Mommy Makeover Consultation</h2>
      <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
        During your consultation, Dr. Jain assesses each area of post-pregnancy change, discusses your goals, and designs a surgical plan that addresses your specific concerns in the most effective and safe combination. You leave with a clear plan, realistic expectations for each component, and practical guidance on timing, recovery, and what support you will need at home.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
        <Button asChild className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base">
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
            Book Consultation
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
        </Button>
        <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
        >
          <a href={TEL_URL}>
              <Phone className="w-5 h-5 mr-2" />
              Call {PHONE_DISPLAY}
            </a>
        </Button>
      </div>
    </div>
  </section>
);

const RelatedProceduresSection = () => {
  const procedures = [
    {
      name: "Tummy Tuck",
      description: "The most consistently included mommy makeover component for abdominal skin, muscle, and fat correction.",
      href: "/tummy-tuck-surgery-mumbai",
    },
    {
      name: "Breast Lift",
      description: "Restore breast position and shape after breastfeeding-related descent and volume change.",
      href: "/breast-lift-surgery-mumbai",
    },
    {
      name: "Liposuction",
      description: "Remove stubborn post-pregnancy fat from waist, flanks, and hips alongside abdominal correction.",
      href: "/liposuction-surgery-mumbai",
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const MommyMakeoverPage = () => (
  <>
    <SEO
      title="Mommy Makeover Surgery | Dr. Vicky Jain"
      description="Post-pregnancy body changes that diet and exercise cannot fix. Dr. Vicky Jain performs customized mommy makeover surgery. Book your consultation today."
      canonical="/mommy-makeover-surgery-mumbai"
    />
    <MommyMakeoverJsonLd />
    <Breadcrumb />
    <HeroSection />
    <WhatIsSection />
    <DrJainApproachSection />
    <PostPregnancyChangesSection />
    <ProceduresIncludedSection />
    <TimingPlanningSection />
    <HowSurgeryPerformedSection />
    <RecoveryTimelineSection />
    <ComparisonSection />
    <GoodCandidateSection />
    <PatientResultsSection />
    <FAQSection />
    <ConsultationCTASection />
    <RelatedProceduresSection />
  </>
);

export default MommyMakeoverPage;
